'use client';
import { ImageApiReference } from '~/components/playground/image/ImageApiReference';
import { ApiButton } from '../affordances/api-button';
import { createControlNetApiCodeSamples } from '../affordances/api-button/create-controlnet-api-code-samples';
import { createImageToImageApiCodeSamples } from '../affordances/api-button/create-image-to-image-api-code-samples';
import { createInferenceApiCodeSamples } from '../affordances/api-button/create-inference-api-code-samples';
import { createTextToImageApiCodeSamples } from '../affordances/api-button/create-text-to-image-api-code-samples';
import { createSd3Examples } from '../affordances/api-button/sd3/createSd3Examples';
import { CustomLink } from '../common/custom-link';
import { useActiveTabState, useRequestState } from './request.context';

export function PlaygroundAPIButton() {
  const { chatState, completionState } = useRequestState();
  const { model, selectedTab, requestState } = useActiveTabState();
  let examples;

  if (/^sd3(-turbo)?$/iu.test(model.id)) {
    examples = [
      ...(selectedTab === 'image'
        ? [
            {
              name: 'Text to Image',
              description: <>Generate an image response for the given prompt.</>,
              samples: createSd3Examples(
                {
                  model,
                  body: requestState,
                },
                'text',
              ),
            },
          ]
        : []),
      ...(selectedTab === 'image-to-image'
        ? [
            {
              name: 'Image to Image',
              description: <>Generate an image response for the given image.</>,
              samples: createSd3Examples(
                {
                  model,
                  body: requestState,
                },
                'image',
              ),
            },
          ]
        : []),
    ];
  } else {
    examples = [
      ...(selectedTab === 'chat'
        ? [
            {
              name: 'Chat',
              description: (
                <>
                  Generate a model response for the given chat conversation.{' '}
                  <CustomLink
                    className="text-primary-200"
                    href="https://readme.fireworks.ai/reference/createchatcompletion"
                  >
                    See full docs
                  </CustomLink>
                </>
              ),
              samples: createInferenceApiCodeSamples({
                model,
                path: '/inference/v1/chat/completions',
                method: 'POST' as const,
                body: {
                  ...chatState,
                  messages: (chatState.messages[chatState.messages.length - 1]?.role === 'assistant'
                    ? chatState.messages.slice(0, -1)
                    : chatState.messages
                  ).map((message) => ({
                    role: message.role,
                    content: message.content,
                    tool_calls: message.tool_calls,
                    tool_call_id: message.tool_call_id,
                    name: message.name,
                  })),
                },
                headers: {
                  Accept: chatState.stream ? 'text/event-stream' : 'application/json',
                  'Content-Type': 'application/json',
                },
              }),
            },
          ]
        : []),
      ...(selectedTab === 'completion'
        ? [
            {
              name: 'Completion',
              description: (
                <>
                  Generate a model response for the given prompt.{' '}
                  <CustomLink className="text-primary-200" href="https://readme.fireworks.ai/reference/createcompletion">
                    See full docs
                  </CustomLink>
                </>
              ),
              samples: createInferenceApiCodeSamples({
                model,
                path: '/inference/v1/completions',
                method: 'POST' as const,
                body: completionState,
                headers: {
                  Accept: completionState.stream ? 'text/event-stream' : 'application/json',
                  'Content-Type': 'application/json',
                },
              }),
            },
          ]
        : []),
      ...(selectedTab === 'image'
        ? [
            {
              name: 'Text to Image',
              description: <>Generate an image response for the given prompt.</>,
              samples: createTextToImageApiCodeSamples({
                model,
                body: requestState,
              }),
            },
          ]
        : []),
      ...(selectedTab === 'image-to-image'
        ? [
            {
              name: 'Image to Image',
              description: <>Generate an image response for the given image.</>,
              samples: createImageToImageApiCodeSamples({
                model,
                body: requestState,
              }),
            },
          ]
        : []),
      ...(selectedTab === 'controlNet'
        ? [
            {
              name: 'Image to Image',
              description: <>Generate a ControlNet response for the given image.</>,
              samples: createControlNetApiCodeSamples({
                model,
                body: requestState,
              }),
            },
          ]
        : []),
    ];
  }

  return (
    <ApiButton
      examples={examples}
      referenceDocs={
        model.type === 'image' && (
          <>
            {!/^sd3(-turbo)?$/iu.test(model.id) && (
              <ImageApiReference
                modelName={model.name}
                defaultResolution={model.generationDefaults.image?.height ?? 1028}
                controlnetValues={model.tags.filter((t) => t === 'canny' || t === 'qr')}
              />
            )}
            {/^sd3(-turbo)?$/iu.test(model.id) && (
              <CustomLink href="https://platform.stability.ai/docs/api-reference#tag/Generate/paths/~1v2beta~1stable-image~1generate~1sd3/post">
                Stability API Documentation
              </CustomLink>
            )}
          </>
        )
      }
    />
  );
}
